"use client";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { HiMagnifyingGlass } from "react-icons/hi2";
import Image from "next/image";
import { LuCompass, LuCalendarClock } from "react-icons/lu";
import { searchItems, SearchResultItem } from "../global-actions";
import { useDebounce } from "~/hooks/useDebounce";
import { GooeySearchInput } from "~/components/v3/themed/SearchInput";
import Modal from "~/components/v3/themed/Modal";
import { Button } from "~/components/v3/themed/Button";
import { useBreakpointV3 } from "~/hooks/useIsMobile";

export const GlobalSearch = ({
    forceCompact = false,
}: {
    forceCompact?: boolean;
}) => {
    const [desktopSearchQuery, setDesktopSearchQuery] = useState("");
    const [results, setResults] = useState<SearchResultItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);
    const debouncedQuery = useDebounce(desktopSearchQuery, 300);
    const isAboveMd = useBreakpointV3("md");

    // Handle desktop search only
    useEffect(() => {
        if (!isAboveMd && !forceCompact) return;

        const fetchResults = async () => {
            if (debouncedQuery.length < 2) {
                setResults([]);
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            try {
                const searchResults = await searchItems(debouncedQuery);
                setResults(searchResults);
            } catch (error) {
                console.error("Search error:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchResults();
    }, [debouncedQuery, isAboveMd]);

    // Close dropdown when clicking outside (desktop only)
    useEffect(() => {
        if (!isAboveMd && !forceCompact) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (
                searchRef.current &&
                !searchRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isAboveMd]);

    const handleDesktopSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDesktopSearchQuery(e.target.value);
        setIsOpen(true);
    };

    const handleSelectResult = (result: SearchResultItem) => {
        window.open(result.url, "_blank");
        setIsOpen(false);
        setIsModalOpen(false);
        setDesktopSearchQuery("");
    };

    const shouldShowResults = isOpen && desktopSearchQuery.length >= 2;

    return (
        <>
            <div
                className={`relative ml-2 mr-2 hidden w-full min-w-[200px] max-w-[320px] ${
                    forceCompact ? "md:hidden" : "md:flex"
                }`}
                ref={searchRef}
            >
                <GooeySearchInput
                    placeholder="Search River"
                    value={desktopSearchQuery}
                    onChange={handleDesktopSearch}
                    iconComponent={
                        <HiMagnifyingGlass className="mr-2 h-4 w-4 self-center" />
                    }
                    isLoading={isLoading}
                    onClick={() => setIsOpen(true)}
                    onFocus={() => setIsOpen(true)}
                />
                {shouldShowResults && (
                    <div className="absolute left-0 right-0 top-12 z-50 mt-1 max-h-[300px] overflow-y-auto rounded-md bg-v3-N-500 shadow-lg">
                        <SearchResultsList
                            results={results}
                            isLoading={isLoading}
                            handleSelectResult={handleSelectResult}
                        />
                    </div>
                )}
            </div>
            <span className={`${forceCompact ? "md:flex" : "md:hidden"}`}>
                <Button
                    variant="ghost"
                    onClick={() => setIsModalOpen(true)}
                    className="p-2 text-white"
                    aria-label="Open search"
                >
                    <HiMagnifyingGlass className="h-5 w-5" />
                </Button>

                <MobileSearchModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSelectResult={handleSelectResult}
                />
            </span>
        </>
    );
};

const SearchResultsList = ({
    results,
    isLoading,
    handleSelectResult,
}: {
    results: SearchResultItem[];
    isLoading: boolean;
    handleSelectResult: (result: SearchResultItem) => void;
}) => {
    return (
        <>
            {results.length === 0 ? (
                <div className="px-4 py-3 text-sm text-white">
                    {isLoading ? "Searching..." : "No results found"}
                </div>
            ) : (
                <ul className="py-1">
                    {results.map((result) => (
                        <li
                            key={`${result.type}-${result.id}`}
                            className="flex cursor-pointer items-center gap-3 px-4 py-2 hover:bg-v3-N-400"
                            onClick={() => handleSelectResult(result)}
                        >
                            {/* Left Column: Image Container */}
                            <div className="h-12 w-12 flex-none">
                                {result.image ? (
                                    <div className="relative h-full w-full overflow-hidden rounded-md">
                                        <Image
                                            src={result.image}
                                            alt={result.name}
                                            fill
                                            sizes="128px"
                                            className="object-cover"
                                            quality={100}
                                        />
                                    </div>
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center rounded-md border border-v3-N-300 bg-v3-N-400">
                                        {result.type === "community" ? (
                                            <LuCompass className="h-5 w-5 text-white" />
                                        ) : (
                                            <LuCalendarClock className="h-5 w-5 text-white" />
                                        )}
                                    </div>
                                )}
                            </div>
                            {/* Right Column: Content */}
                            <div className="min-w-0 flex-1">
                                <p className="break-words text-sm font-medium text-white">
                                    {result.name}
                                </p>
                                <p className="break-words text-xs text-v3-N-200">
                                    {result.type === "community"
                                        ? "Community"
                                        : "Event"}{" "}
                                    • {result.slug}
                                </p>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
};

// Mobile search modal component
const MobileSearchModal = ({
    isOpen,
    onClose,
    onSelectResult,
}: {
    isOpen: boolean;
    onClose: () => void;
    onSelectResult: (result: SearchResultItem) => void;
}) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [results, setResults] = useState<SearchResultItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const debouncedQuery = useDebounce(searchQuery, 300);

    // Reset search when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSearchQuery("");
            setResults([]);
        }
    }, [isOpen]);

    // Handle search
    useEffect(() => {
        const fetchResults = async () => {
            if (debouncedQuery.length < 2) {
                setResults([]);
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            try {
                const searchResults = await searchItems(debouncedQuery);
                setResults(searchResults);
            } catch (error) {
                console.error("Search error:", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (isOpen) {
            fetchResults();
        }
    }, [debouncedQuery, isOpen]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handleSelectResult = (result: SearchResultItem) => {
        onSelectResult(result);
        setSearchQuery("");
    };

    return (
        <Modal
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
            title="Search"
            className="h-[80vh] max-h-[80vh] bg-v3-N-600"
        >
            <div className="flex h-full flex-col">
                <GooeySearchInput
                    placeholder="Search communities and events..."
                    value={searchQuery}
                    onChange={handleSearch}
                    iconComponent={
                        <HiMagnifyingGlass className="mr-2 h-4 w-4" />
                    }
                    isLoading={isLoading}
                    autoFocus
                />
                <div className="flex-1 overflow-y-auto">
                    {searchQuery.length >= 2 ? (
                        <SearchResultsList
                            results={results}
                            isLoading={isLoading}
                            handleSelectResult={handleSelectResult}
                        />
                    ) : (
                        <div className="px-4 py-3 text-sm text-white">
                            Enter at least 2 characters to search
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};
